import { type FormEvent, type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { Button, cn, Label } from '@/shared/ui';
import { useDeclineQuote } from '../model/use-decline-quote';

export interface DeclineQuoteFormProps {
  readonly quoteRequestId: string;
  /** 닫기 — 거절하지 않고 확인 단계를 닫는다. */
  readonly onClose: () => void;
}

const REASON_MAX_LENGTH = 500;

/**
 * 견적 거절 확인 단계(견적 설계 §7-7) — 되돌릴 수 없음을 알리고 선택 사유(500자 이하)를 받는다.
 * 사유는 운영자가 관리자 상세에서 본다. 열리면 사유 입력란에 포커스를 둔다.
 */
export const DeclineQuoteForm = ({
  quoteRequestId,
  onClose,
}: DeclineQuoteFormProps): ReactElement => {
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const noticeId = `${reasonId}-notice`;
  const errorId = `${reasonId}-error`;
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const { decline, isPending, fieldError, error } = useDeclineQuote();
  const message = fieldError ?? error;

  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    await decline({ quoteRequestId, reason });
  };

  return (
    <form
      aria-label="견적 거절"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="flex flex-col gap-3 border border-destructive/40 p-5"
      noValidate
    >
      <p id={noticeId} className="text-sm">
        이 견적을 거절하면 되돌릴 수 없습니다. 거절 사유를 남기면 운영자가 다음 견적에 참고합니다.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={reasonId}>거절 사유(선택)</Label>
        <textarea
          ref={reasonRef}
          id={reasonId}
          rows={3}
          maxLength={REASON_MAX_LENGTH}
          value={reason}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={message ? `${noticeId} ${errorId}` : noticeId}
          className={cn(
            'w-full rounded-2xl border border-input bg-background px-4 py-2 text-sm transition-colors',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            fieldError && 'border-destructive focus-visible:ring-destructive',
          )}
          placeholder="예: 예산이 줄었습니다."
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      {message ? (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {message}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="destructive" isLoading={isPending}>
          거절하기
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          닫기
        </Button>
      </div>
    </form>
  );
};
